<template>
  <div
    :class="
      classes(
        n(),
        {
          [n('--disabled')]: mergedDisabled,
          [n('--focused')]: isFocused,
          [n('--in-form-item')]: inFormItem,
        }
      )
    "
  >
    <div :class="n('wrapper')">
      <div :class="n('row')">
        <!-- 输入框容器 -->
        <div :class="n('input-wrapper')">
          <!-- 前缀 -->
          <div
            v-if="$slots.prefix"
            :class="n('prefix')"
          >
            <slot name="prefix" />
          </div>

          <!-- 原生input -->
          <input
            ref="inputRef"
            :class="n('native')"
            :type="type"
            :value="innerValue"
            :placeholder="placeholder"
            :placeholder-class="n('placeholder')"
            :disabled="mergedDisabled"
            v-bind="readonly ? { readonly: true } : {}"
            :maxlength="maxlength"
            :name="name"
            :autofocus="autofocus"
            :inputmode="inputMode"
            :adjust-position="adjustPosition"
            :style="{ textAlign: inputAlign }"
            @input="handleInput"
            @focus="handleFocus"
            @blur="handleBlur"
            @change="handleChange"
            @keydown="handleKeydown"
            @keyup="handleKeyup"
          >

          <!-- 后缀 -->
          <div
            v-if="$slots.suffix"
            :class="n('suffix')"
          >
            <slot name="suffix" />
          </div>
        </div>

        <!-- 操作区 -->
        <div
          v-if="$slots.action"
          :class="n('action')"
          @click="handleActionClick"
        >
          <slot name="action" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, inject } from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import { useVModel } from '../hooks/use-v-model';
import { FORM_ITEM_KEY } from '../form/constants';

const { name, n, classes } = createNamespace('input');

export default defineComponent({
  name,
  props,
  emits,
  setup(props, { emit }) {
    const inputRef = ref<HTMLInputElement>();
    const isFocused = ref(false);
    const innerValue = useVModel<string | number>(props);

    const formItemContext = inject(FORM_ITEM_KEY, null);
    const inFormItem = computed(() => !!formItemContext);

    const mergedDisabled = computed(() => {
      if (formItemContext) return formItemContext.disabled.value || props.disabled;
      return props.disabled;
    });

    const handleInput = (event: Event) => {
      const target = event.target as HTMLInputElement;
      innerValue.value = target.value;
    };

    const handleFocus = (event: FocusEvent) => {
      isFocused.value = true;
      formItemContext?.onControlFocus?.();
      emit('focus', event);
    };

    const handleBlur = (event: FocusEvent) => {
      isFocused.value = false;
      formItemContext?.onControlBlur?.();
      emit('blur', event);
    };

    const handleChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      emit('change', target.value);
    };

    const handleActionClick = (event: MouseEvent) => {
      if (mergedDisabled.value) {
        return;
      }
      emit('action-click', event);
    };

    const handleKeydown = (event: KeyboardEvent) => {
      emit('keydown', event);
    };

    const handleKeyup = (event: KeyboardEvent) => {
      emit('keyup', event);
    };

    const focus = () => {
      if (inputRef.value) {
        inputRef.value.focus();
      }
    };

    const blur = () => {
      if (inputRef.value) {
        inputRef.value.blur();
      }
    };

    return {
      n,
      classes,
      inputRef,
      isFocused,
      innerValue,
      inFormItem,
      mergedDisabled,
      handleInput,
      handleFocus,
      handleBlur,
      handleChange,
      handleActionClick,
      handleKeydown,
      handleKeyup,
      focus,
      blur,
    };
  },
});
</script>
